import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { getUsersByApplication } from '../../services/users/usersService';
import { getApplicationById } from '../../services/applications/applicationsService';
import { UserDto, UserStatus } from '../../services/users/types';
import { ApplicationDto } from '../../services/applications/types';
import Pagination from '../../components/UI/Pagination';

const ApplicationUsers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState<UserDto[]>([]);
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    if (id && auth.user) {
      fetchApplicationData();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth.user, currentPage]);

  const fetchApplicationData = async () => {
    if (!id || !auth.user) return;
    
    try {
      const appData = await getApplicationById(auth.user, id);
      setApplication(appData);
    } catch (error) {
      console.error('Error fetching application:', error);
    }
  };

  const fetchUsers = async () => {
    if (!id || !auth.user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching users for page:', currentPage, 'pageSize:', pageSize);
      const response = await getUsersByApplication(auth.user, id, currentPage, pageSize);
      console.log('API Response:', response);
      
      setUsers(response.content || []);
      const totalElements = response.total_elements || 0;
      setTotalItems(totalElements);
      setTotalPages(Math.ceil(totalElements / pageSize));
      
      console.log('Total elements:', totalElements);
      console.log('Total pages:', Math.ceil(totalElements / pageSize));
      console.log('Current page:', currentPage);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToApplication = () => {
    navigate(`/applications/${id}`);
  };

  const getStatusBadgeClasses = (status?: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVATED:
        return 'bg-green-100 text-green-700';
      case UserStatus.INACTIVATED:
        return 'bg-gray-100 text-gray-700';
      case UserStatus.BLOCKED:
        return 'bg-red-100 text-red-700';
      case UserStatus.RECONFIGURE:
        return 'bg-yellow-100 text-yellow-700';
      case UserStatus.ONBOARDING_IN_PROGRESS:
        return 'bg-blue-100 text-blue-700';
      case UserStatus.TICKET_OPENING:
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && !users.length) {
    return (
      <div>
        <PageHeader
          title="Application Users"
          breadcrumbs={[
            { label: 'Applications', to: '/applications' },
            { label: 'Users' }
          ]}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Users - ${application?.name || 'Loading...'}`}
        breadcrumbs={[
          { label: 'Applications', to: '/applications' },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: 'Users' }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={handleBackToApplication}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Application
          </Button>
        }
      />

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={fetchUsers}
            >
              Retry
            </Button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No users found for this application.</p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <Link 
                          to={`/users/${user.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {user.first_name || 'N/A'} {user.last_name || ''}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(user.status)}`}>
                          {user.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.phone_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link to={`/users/${user.id}`}>
                            <Button variant="ghost" size="sm" title="View">
                              <i className="fas fa-eye"></i>
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="py-3 px-6 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ApplicationUsers;